import React from 'react';
import { ArrowLeft, Shield, Users, Eye, FileCheck, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import MetaTags from '../../components/SEO/MetaTags';
import { pageConfigs, generateBlogPostSchema } from '../../utils/seo';

const WhyNotation: React.FC = () => {
  const blogSchema = generateBlogPostSchema({
    title: "Why Notation? We Are More Than Just an AI Scribe",
    description: "Notation gives your EMR superpowers without hefty integration costs. Privacy-first AI, PT-in-the-loop documentation, computer vision, and Home Health workflows.",
    datePublished: "2026-02-12",
    dateModified: "2026-02-12",
    author: "Fownd Team",
    url: "/blogs/why-notation-more-than-ai-scribe",
    image: "/blog/Booth1.png"
  });

  return (
    <>
      <MetaTags
        title={pageConfigs.blogWhyNotation.title}
        description={pageConfigs.blogWhyNotation.description}
        keywords={pageConfigs.blogWhyNotation.keywords}
        canonical={pageConfigs.blogWhyNotation.canonical}
        ogImage="/blog/Booth1.png"
        ogType="article"
        schemaData={blogSchema}
        additionalMeta={[
          { property: "article:author", content: "Fownd Team" },
          { property: "article:published_time", content: "2026-02-12T00:00:00Z" },
          { property: "article:modified_time", content: "2026-02-12T00:00:00Z" },
          { property: "article:section", content: "Healthcare Technology" },
          { property: "article:tag", content: "Notation" },
          { property: "article:tag", content: "AI Documentation" },
          { property: "article:tag", content: "Physical Therapy" },
          { property: "article:tag", content: "Clinical Workflow" },
          { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" }
        ]}
      />

      <div className="bg-white min-h-screen">
        {/* Hero - Gradient with subtle pattern */}
        <div className="bg-gradient-to-br from-[#1F3D3B] via-[#2a5a56] to-[#1F3D3B] text-white pt-36 pb-12 mb-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
            <p className="text-[#7dd3fc] text-sm font-medium uppercase tracking-[0.2em] mb-4">Blog Post</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 font-productSans">
              Why Notation? We Are More Than Just an AI Scribe.
            </h1>
            <p className="text-white/90 text-lg mb-6">
              Real utility for clinicians. Notes that are auditable, compliant, and secure.
            </p>
            <p className="text-white/70 text-sm">
              By Fownd Team · February 12, 2026
            </p>
          </div>
        </div>

        {/* Content - Wider for larger images */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-20">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#1F3D3B] text-[#1F3D3B] hover:bg-[#1F3D3B] hover:text-white transition-all duration-300 mb-12 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blogs
          </Link>

          {/* Intro */}
          <article>
            <p className="text-gray-700 leading-relaxed text-lg mb-12">
              We are not just another transcriber; we are here to serve clinicians, not insurance companies. Notation gives your current EMR superpowers without hefty integration costs. The result? Notes that are auditable, compliant, and secure. We give you back hours of your day, whether that is for seeing more patients or finally getting home on time.
            </p>

            {/* Point 1: Privacy */}
            <section className="mb-16">
              <div className="flex items-start gap-4 mb-6">
                <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#1F3D3B]/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#1F3D3B]" />
                </span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Privacy-First, Secure AI</h2>
                  <ul className="space-y-6 text-gray-700">
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">No Public Models:</p>
                      <p className="leading-relaxed">Notation runs on FOWND's own PT-focused model. We do not use OpenAI or third-party foundation models.</p>
                    </li>
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">Your Data Stays Yours:</p>
                      <p className="leading-relaxed">Patient data never leaves your clinic environment and is never used to train external models.</p>
                    </li>
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">HIPAA Native:</p>
                      <p className="leading-relaxed">We are built for compliance from day one, with SOC 2 alignment as a standard.</p>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Point 2: PT-in-the-loop + Translation */}
            <section className="mb-16">
              <div className="flex items-start gap-4 mb-6">
                <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#619ed3]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#619ed3]" />
                </span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">2. A "PT-in-the-loop" Assistant</h2>
                  <ul className="space-y-6 text-gray-700 mb-8">
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">You Stay in Control:</p>
                      <p className="leading-relaxed">We do not believe in "hands-off automation." Notation supports the full visit while keeping you in the driver's seat with structured documentation and compliance guardrails.</p>
                    </li>
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">Language Barrier Breaker:</p>
                      <p className="leading-relaxed">Built-in real-time translation allows a Spanish-speaking PT to treat a Japanese-speaking patient seamlessly, generating a compliant English note by the end of the session.</p>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col items-center gap-6">
                <figure className="rounded-2xl overflow-hidden shadow-xl border border-gray-100 max-w-2xl w-full mx-auto">
                  <img
                    src="/blog/Polly2.png"
                    alt="Split view: translation chat and structured documentation"
                    className="w-full"
                  />
                  <figcaption className="px-6 py-4 bg-gray-50 text-gray-600 text-sm text-center">
                    Translation flows into structured, reimbursable documentation.
                  </figcaption>
                </figure>
                <figure className="rounded-2xl overflow-hidden shadow-xl border border-gray-100 max-w-[360px] w-full mx-auto">
                  <img
                    src="/blog/Polly1.png"
                    alt="Real-time translation from Spanish and Japanese to English"
                    className="w-full"
                  />
                  <figcaption className="px-6 py-4 bg-gray-50 text-gray-600 text-sm text-center">
                    Foreign language input transforms into professional English medical terminology.
                  </figcaption>
                </figure>
              </div>
            </section>

            {/* Point 3: Computer Vision */}
            <section className="mb-16">
              <div className="flex items-start gap-4 mb-6">
                <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#5c91a4]/10 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-[#5c91a4]" />
                </span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Computer Vision for Real Clinics</h2>
                  <ul className="space-y-6 text-gray-700 mb-8">
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">No Wearables Needed:</p>
                      <p className="leading-relaxed">Vision supports objective capture and progress insights using just your laptop or phone camera.</p>
                    </li>
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">Goniometry Built-In:</p>
                      <p className="leading-relaxed">We currently support ROM capture (with functional movement coming soon), meaning you get objective data without needing new equipment.</p>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col items-center gap-6">
                <figure className="rounded-2xl overflow-hidden shadow-xl border border-gray-100 max-w-2xl w-full mx-auto">
                  <img
                    src="/blog/CV1.png"
                    alt="AROM capture interface with live tracking and captured angles"
                    className="w-full"
                  />
                  <figcaption className="px-6 py-4 bg-gray-50 text-gray-600 text-sm text-center">
                    Live AROM capture feeds directly into structured documentation.
                  </figcaption>
                </figure>
                <figure className="rounded-2xl overflow-hidden shadow-xl border border-gray-100 max-w-2xl w-full mx-auto">
                  <img
                    src="/blog/CV2.png"
                    alt="ROM measurement with skeletal overlay and joint angle display"
                    className="w-full"
                  />
                  <figcaption className="px-6 py-4 bg-gray-50 text-gray-600 text-sm text-center">
                    ROM capture with webcam. No lab or special sensors required.
                  </figcaption>
                </figure>
              </div>
            </section>

            {/* Point 4: Defensibility */}
            <section className="mb-16">
              <div className="flex items-start gap-4 mb-6">
                <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#1F3D3B]/10 flex items-center justify-center">
                  <FileCheck className="w-5 h-5 text-[#1F3D3B]" />
                </span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Designed by a DPT for Defensibility</h2>
                  <ul className="space-y-6 text-gray-700">
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">Clinical Guardrails:</p>
                      <p className="leading-relaxed">Unlike generic LLMs that write fluffy paragraphs, our "clinical skill engine" ensures outputs are structured, defensible, and audit-ready.</p>
                    </li>
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">Workflow Intuition:</p>
                      <p className="leading-relaxed">The interface is built for how PTs actually document, with clear controls that keep you on track.</p>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Point 5: Home Health */}
            <section className="mb-16">
              <div className="flex items-start gap-4 mb-6">
                <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#486633]/10 flex items-center justify-center">
                  <Home className="w-5 h-5 text-[#486633]" />
                </span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Built for Home Health Realities</h2>
                  <ul className="space-y-6 text-gray-700">
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">OASIS Ready:</p>
                      <p className="leading-relaxed">We support Start of Care workflows and OASIS mapping to reduce the Home Health documentation burden.</p>
                    </li>
                    <li>
                      <p className="text-[#1F3D3B] font-semibold mb-1">Instant HEP:</p>
                      <p className="leading-relaxed">AI generates a rich Home Exercise Program within the note, allowing PTs to finish comprehensive documentation in under 2 minutes.</p>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-end mt-8">
              <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-center w-full max-w-4xl">
                <div className="space-y-5">
                  <p className="text-gray-700 leading-loose text-xl">
                    Notation brings full Home Health workflows to your phone. Register patients, set visit types like Start of Care, and complete OASIS documentation wherever you are—without being tied to a desk.
                  </p>
                  <p className="text-gray-700 leading-loose text-xl">
                    See how the mobile interface streamlines New Patient Registration, Visit Type selection, and consent—built for the realities of field-based care.
                  </p>
                </div>
                <figure className="rounded-2xl overflow-hidden shadow-xl border border-gray-100 max-w-[340px] mx-auto lg:mx-0 bg-white">
                  <video
                    src="/blog/NotationMobile.mp4"
                    controls
                    playsInline
                    className="w-full block object-cover align-bottom"
                  />
                  <figcaption className="px-6 py-2 bg-gray-50 text-gray-600 text-sm text-center">
                    OASIS and documentation on mobile. Built for Home Health workflows.
                  </figcaption>
                </figure>
              </div>
              </div>
            </section>

            {/* What's Next */}
            <section className="mb-16 p-8 rounded-2xl bg-gradient-to-br from-[#1F3D3B]/5 to-[#619ed3]/5 border border-[#1F3D3B]/10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What is Next?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Forget expensive integrations. We are launching a smart browser extension that learns your EMR's UI and transfers Notation outputs directly into the fields—powered by our secure AI.
              </p>
            </section>

            {/* CSM Proof of Life */}
            <section className="mb-16">
              <p className="text-gray-700 leading-relaxed mb-8">
                Christina Rama, DPT, our founder, is currently demonstrating live evaluations on a 10-year-old laptop to prove our efficiency, while other tools rely on pre-recorded videos to avoid the noise.
              </p>

              <figure className="rounded-2xl overflow-hidden shadow-xl border border-gray-100 max-w-2xl w-full mx-auto">
                <img
                  src="/blog/Booth1.png"
                  alt="Real-time evaluation on a vintage laptop at CSM"
                  className="w-full"
                />
                <figcaption className="px-6 py-4 bg-gray-50 text-gray-600 text-sm text-center italic">
                  Real-time evaluation on a vintage laptop at CSM. No canned videos, no quiet room required.
                </figcaption>
              </figure>
            </section>

            {/* CTA */}
            <div className="rounded-2xl p-8 sm:p-10 bg-gradient-to-br from-[#1F3D3B] to-[#2a5a56] text-white text-center">
              <h3 className="text-2xl font-bold mb-3">Try it on your phone</h3>
              <p className="text-white/80 mb-6">No email required.</p>
              <a
                href="https://notation.fownd.care/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white text-[#1F3D3B] font-semibold hover:bg-white/90 transition-colors"
              >
                Try Notation
              </a>
            </div>
          </article>

          {/* Back button */}
          <div className="mt-12">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#1F3D3B] text-[#1F3D3B] hover:bg-[#1F3D3B] hover:text-white transition-all duration-300 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blogs
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default WhyNotation;
